// Auto-generated lightweight class for ControlFlow
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { ControlFlowReadAPI, ControlFlowWriteAPI, ControlFlowReadAPISchema, ControlFlowWriteAPISchema } from '../types/controlflow';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasMembers, Specializes } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from './sysmlnamespace';
import { SysMLType } from './sysmltype';



export interface ControlFlow extends Partial<ControlFlowReadAPI> {}
export class ControlFlow extends NeoNodeBase<ControlFlowReadAPI, ControlFlowWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "ControlFlow";
  static readonly readSchema = ControlFlowReadAPISchema;
  static readonly writeSchema = ControlFlowWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    general: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "out" },
    specific: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "in" },
  };

  protected get modelName(): string {
    return 'ControlFlow';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return ControlFlow.label;
  }


  constructor(data: Partial<ControlFlowWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: ControlFlowReadAPI, loadContext?: Record<string, any>): ControlFlow {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, general, specific, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = ControlFlowReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, general: general, specific: specific };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return ControlFlow.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'general', 'SysMLType', ModelRegistry);
    loadManyRelationship(processed, 'specific', 'SysMLType', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new ControlFlow(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'general' - Python ORM style (cached)
  get general(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('general', () => {
      return new RelationshipManager(this, 'general', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'specific' - Python ORM style (cached)
  get specific(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('specific', () => {
      return new RelationshipManager(this, 'specific', 'many', Specializes as typeof Relationship);
    });
  }

}
